import { useState } from "react";
import { Search, PackageSearch, MapPin, CalendarClock, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const STATUS_COLORS = {
  Delivered: "bg-green-100 text-green-700 ring-green-200",
  "In Transit": "bg-amber-100 text-amber-700 ring-amber-200",
  Delayed: "bg-red-100 text-red-700 ring-red-200",
  Booked: "bg-navy-50 text-navy ring-blue-200",
  "At Port": "bg-blue-100 text-blue-700 ring-blue-200",
};

export const TrackingWidget = () => {
  const [id, setId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const track = async (e) => {
    e.preventDefault();
    if (!id.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(id.trim())}`);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not fetch shipment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="track" className="relative z-20 -mt-24 px-4 sm:px-6 lg:px-8" data-testid="tracking-section">
      <div className="mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white p-6 shadow-xl shadow-navy/10 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white">
            <PackageSearch size={22} />
          </span>
          <div>
            <h2 className="font-display text-xl font-black text-navy">Track Your Shipment</h2>
            <p className="text-sm text-muted-foreground">Enter your tracking ID to see live status.</p>
          </div>
        </div>

        <form onSubmit={track} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="e.g. ROK1A2B3C4D"
              data-testid="tracking-input"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-12 pr-4 text-sm font-medium uppercase text-navy outline-none transition focus:border-navy focus:ring-2 focus:ring-navy/20"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="tracking-submit-btn"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brandred px-7 py-3.5 text-sm font-bold text-white transition-transform hover:bg-brandred-700 active:scale-95 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search size={16} />} Track
          </button>
        </form>

        {error && (
          <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-brandred" data-testid="tracking-error">
            {error}
          </p>
        )}

        {result && (
          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5" data-testid="tracking-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500">Tracking ID</p>
                <p className="font-display text-lg font-black text-navy">{result.tracking_id}</p>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ring-1 ${
                  STATUS_COLORS[result.status] || "bg-gray-100 text-gray-700 ring-gray-200"
                }`}
              >
                {result.status}
              </span>
            </div>

            <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <p className="flex items-center gap-2 text-navy"><MapPin size={16} className="text-gold" /> {result.origin} → {result.destination}</p>
              {result.current_location && (
                <p className="flex items-center gap-2 text-navy"><MapPin size={16} className="text-gold" /> Now at: {result.current_location}</p>
              )}
              {result.estimated_delivery && (
                <p className="flex items-center gap-2 text-navy"><CalendarClock size={16} className="text-gold" /> ETA: {result.estimated_delivery}</p>
              )}
            </div>

            {result.events?.length > 0 && (
              <ol className="mt-5 space-y-4 border-l-2 border-navy/15 pl-5">
                {result.events.map((ev, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-gold ring-4 ring-white" />
                    <p className="text-sm font-bold text-navy">{ev.status}</p>
                    <p className="text-xs text-muted-foreground">{ev.location} · {ev.date}</p>
                    {ev.note && <p className="text-xs text-gray-500">{ev.note}</p>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
